import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { FadeUp } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-5"];
const CAT_LABEL = { concert: "演唱会", festival: "音乐节", corporate: "商业活动", production: "音乐制作" };

export default function Highlights() {
  const { lang } = useLang();
  const zh = lang === "zh";
  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => api.get("/projects").then((r) => r.data),
    staleTime: 60_000,
  });
  const featured = projects
    .filter((p) => p.home_featured)
    .sort((a, b) => (a.home_order ?? 9999) - (b.home_order ?? 9999))
    .slice(0, 8);
  const list = featured.length ? featured : projects.filter((p) => p.featured).slice(0, 8);

  return (
    <section data-testid="highlights" className="py-24 md:py-32 bg-[#050505]">
      <div className="max-w-[1800px] mx-auto px-4 md:px-8">
        <FadeUp className="flex items-end justify-between gap-6 border-b border-white/10 pb-6 mb-10 md:mb-16">
          <div>
            <div className="font-mono2 text-xs text-[#ffde00] tracking-[0.3em] mb-3">02 — SELECTED WORKS</div>
            <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] text-[#f5f5f5]">{zh ? "精选演出" : "SELECTED WORKS"}</h2>
          </div>
          <Link to="/works" data-testid="highlights-all" className="btn-ghost !py-2.5 whitespace-nowrap">
            {zh ? "全部作品" : "ALL WORKS"}
            <ArrowRight size={14} />
          </Link>
        </FadeUp>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {list.map((p, i) => (
            <motion.div
              key={p.id || i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className={`group relative overflow-hidden border border-white/10 hover:border-[#ffde00] transition-colors duration-300 ${SPANS[i % SPANS.length]}`}
              data-testid={`highlight-card-${i}`}
            >
              <Link to={`/works/${p.id}`} className="block">
                <div className={`w-full overflow-hidden ${SPANS[i % SPANS.length] === "md:col-span-4" ? "h-[280px] md:h-[360px]" : "h-[300px] md:h-[440px]"}`}>
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition duration-700 ease-out"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 flex items-end justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-mono2 text-[10px] md:text-xs text-[#ffde00] tracking-[0.25em]">
                      {p.year || "—"} · {p.location || CAT_LABEL[p.category] || ""}
                    </div>
                    <h3 className="font-display text-xl md:text-2xl uppercase leading-tight mt-1.5 text-[#f5f5f5] truncate">{p.title}</h3>
                  </div>
                  <ArrowUpRight size={22} className="shrink-0 text-[#ffde00] opacity-0 -translate-x-2 translate-y-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-300" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
